import React, {useEffect} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {Animate, Box, Media, useMediaQuery} from '@tenancy.nz/design-system';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import {pageView} from '../../analytics';
import CollapsibleText from '../../components/CollapsibleText';
import DeletedProperty from '../../components/DeletedProperty';
import Footer from '../../components/Footer';
import FormPaper from '../../components/FormPaper';
import Nameplate from '../../components/Nameplate';
import IconLabel from '../../components/IconLabel';
import PageHero from '../../components/PageHero';
import PropertyAttributes from '../../components/PropertyAttributes';
import NotFound from '../../components/NotFound';
import Map from '../../components/Map';
import Section from '../../components/Section';
import SectionHeader from '../../components/SectionHeader';
import BookingFeature from '../booking/BookingFeature';
import {useInitAgencyBySlugQuery} from '../../api/main/agency';
import {env} from '../../utils/helpers';

function ListingDetailFeature() {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const location = useLocation();
  const {id} = useParams();

  const {
    data: {prepared} = {prepared: {}},
    isError,
    isLoading,
  } = useInitAgencyBySlugQuery(id, {
    skip: !id,
  });
  const {agency, property} = prepared;

  useEffect(() => {
    // Log the page view in analytics
    pageView({
      path: location.pathname,
      title: document.title,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial loading view
  if (isLoading) {
    return <Nameplate skeleton />;
  }

  // If no access, show 404 page
  if (!isLoading && (!agency.hasBookMeAccess || isError)) {
    return <NotFound />;
  }

  // Show deleted property page
  if (!isLoading && property.status === 'deleted') {
    return (
      <DeletedProperty
        url={`${env('REACT_APP_URL')}/tps${agency.clientCode}`}
      />
    );
  }

  const header = (
    <SectionHeader
      heading={property.addressPart1}
      subheading={property.addressPart2}
      mobile={isMobile}
    />
  );

  const attributesContent = (
    <React.Fragment>
      <IconLabel
        icon={<MonetizationOnIcon color="primary" />}
        size="24px"
        text={property.rent}
        sx={{mb: {xs: '8px', md: '16px'}, mt: '6px'}}
      />
      <PropertyAttributes
        availableAt={property.dateAvailableDisplay}
        noOfBedrooms={property.bedrooms}
        noOfBathrooms={property.bathrooms}
        noOfCarparks={property.parkingSpaces}
        sx={{mb: {xs: '24px', md: '8px'}}}
      />
    </React.Fragment>
  );

  const mapPopupContent = (
    <Box
      sx={{
        display: 'flex',
        gap: '15px',
        alightItems: 'flex-start',
        mt: '20px',
      }}>
      <Box width="70px" height="55px">
        <Media src={property.thumbphoto} />
      </Box>
      <Box flex="1">
        <PropertyAttributes
          address={property.address}
          noOfBedrooms={property.bedrooms}
          noOfBathrooms={property.bathrooms}
          noOfCarparks={property.parkingSpaces}
          rent={property.rent}
          xs={{gap: '4px'}}
        />
      </Box>
    </Box>
  );

  const content = (
    <React.Fragment>
      {!isMobile && attributesContent}
      <CollapsibleText text={property.description} />
      <Map
        latitude={property.latitude}
        address={property.address}
        longitude={property.longitude}
        popupContent={mapPopupContent}
        title="Location:"
        sx={{my: '24px'}}
      />
    </React.Fragment>
  );

  const BookingFormComponent = isMobile ? Box : FormPaper;
  const rightContent = (
    <React.Fragment>
      {isMobile && header}
      {isMobile && attributesContent}
      <BookingFormComponent>
        <BookingFeature />
      </BookingFormComponent>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <Nameplate
        skeleton={isLoading}
        title="You are booking through:"
        heading={agency.companyName}
        subHeading={agency.city}
        logo={agency.logo}
      />
      <PageHero fixed slides={property.images} />
      <Animate>
        <Section
          header={!isMobile ? header : undefined}
          leftContent={content}
          rightContent={rightContent}
        />
      </Animate>
      <Footer />
    </React.Fragment>
  );
}

export default ListingDetailFeature;
